import React, { ChangeEvent, useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";

import Box from "veneer/Box";
import Button from "veneer/Button";
import ClickAwayListener from "veneer/ClickAwayListener";
import Divider from "veneer/Divider";
import FocusTrap from "veneer/FocusTrap";
import FormControlLabel from "veneer/FormControlLabel";
import InitialsAvatar from "veneer/InitialsAvatar";
import { PAGE_HEADER_HEIGHT } from "veneer/PageHeader";
import PaperPopper, { PaperPopperProps } from "veneer/PaperPopper";
import RadioGroup from "veneer/RadioGroup";
import { showError } from "veneer/SnackBar";
import Stack from "veneer/Stack";
import styled from "veneer/styled";
import { Colors } from "veneer/Theme";
import Typography from "veneer/Typography";

import { useAppSelector } from "cortex/ui/src/hooks";
import RadioButton from "cortex/ui/src/ui/radio-button";
import { User } from "cortex/ui/src/user/model";

interface AccountManagementPopoverProps {
  anchorEl: PaperPopperProps["anchorEl"];
  hideSettings?: boolean;
  onOpenChange: (open: boolean) => void;
  open: boolean;
  user: User;
}

export interface AssumableAccount {
  name: string;
  id: number;
}

export const AccountManagementPopover = ({
  anchorEl,
  hideSettings,
  onOpenChange,
  open,
  user,
}: AccountManagementPopoverProps) => {
  const handleClose = (event: MouseEvent | TouchEvent) => {
    if (!anchorEl?.contains(event.target as HTMLElement)) {
      onOpenChange(false);
    }
  };

  const assumableAccounts = useAppSelector(
    (state: any) => state.global.auth.user.accounts || [],
  );
  const currentAccount = useAppSelector(
    (state: any) => state.global.auth.user.account,
  );
  const currentAccountId = useAppSelector(
    (state: any) => state.global.auth.user.account.id,
  );

  const [selectedAccountId, setSelectedAccountId] = useState<number | string>(
    "",
  );
  const handleRadioChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSelectedAccountId(Number(event.target.value));
  };
  const hasMultiAccount = assumableAccounts.length > 1;
  const switchAccounts = async () => {
    try {
      const response = await fetch(
        `/api/accounts/${selectedAccountId}/switch`,
        {
          method: "GET",
          redirect: "follow",
        },
      );

      if (response.status === 302 || response.ok) {
        window.location.href = response.url || "/overview";
      } else if (response.status === 403) {
        showError("Account user is disabled.");
      } else {
        throw new Error(`Unexpected response code: ${response.status}`);
      }
    } catch (error) {
      console.error("Failed to switch accounts:", error);
    }
  };

  useEffect(() => {
    if (currentAccountId) {
      setSelectedAccountId(currentAccountId);
    }
  }, [currentAccountId]);

  return (
    <UserAccountPopper
      anchorEl={anchorEl}
      dataTestId="account-manager"
      disablePortal={false}
      elevation={3}
      open={open}
      placement="right-end"
    >
      <ClickAwayListener onClickAway={handleClose}>
        <FocusTrap disableEnforceFocus open={open}>
          <Box tabIndex={-1}>
            {hasMultiAccount && (
              <ScrollableUserWrapper>
                <Box>
                  <RadioGroup
                    dataTestId="account-list"
                    onChange={handleRadioChange}
                    value={selectedAccountId}
                  >
                    <Stack divider={<Divider />}>
                      {assumableAccounts.map((account: AssumableAccount) => (
                        <AccountRadioButtonLabel
                          control={<RadioButton />}
                          key={account.id}
                          label={
                            <UserAccountLabel account={account} user={user} />
                          }
                          labelPlacement="start"
                          value={account.id}
                        />
                      ))}
                    </Stack>
                  </RadioGroup>
                </Box>
              </ScrollableUserWrapper>
            )}

            {!hasMultiAccount && (
              <Box>
                <Stack divider={<Divider />}>
                  <Stack direction="column" px={2} py={1}>
                    <UserAccountLabel account={currentAccount} user={user} />
                    {!hideSettings && (
                      <Typography mt={1}>
                        <RouterLink to="/settings">Change Password</RouterLink>
                      </Typography>
                    )}
                  </Stack>
                </Stack>
              </Box>
            )}

            <AccountSwitchActions>
              <Box display="flex" flexGrow={1} gap={1} justifyContent="right">
                {hasMultiAccount && (
                  <Button
                    dataTestId="change-account-button"
                    disabled={selectedAccountId === currentAccountId}
                    onClick={switchAccounts}
                    size="small"
                  >
                    Change Account
                  </Button>
                )}

                <a href="/api/auth/logout" style={{ textDecoration: "none" }}>
                  <Button size="small" variant="outlined">
                    Log Out
                  </Button>
                </a>
              </Box>
            </AccountSwitchActions>
          </Box>
        </FocusTrap>
      </ClickAwayListener>
    </UserAccountPopper>
  );
};

const AccountSwitchActions = styled(Box)(({ theme }) => ({
  alignItems: "center",
  borderTop: `1px solid ${theme.palette.divider}`,
  display: "flex",
  gap: theme.spacing(1),
  justifyContent: "space-between",
  padding: theme.spacing(2),
  width: "100%",
}));

const UserAccountLabel = ({ account, user }: UserAccountLabelProps) => {
  return (
    <Box alignItems="center" display="flex">
      <Box mr={2}>
        <InitialsAvatar fullName={user.email} size="large" />
      </Box>
      <Stack direction="column" justifyContent="space-between" width={200}>
        <UserAccountTypography color={Colors.gray1000}>
          {account.name}
        </UserAccountTypography>
        <UserAccountTypography variant="body2">
          {user.email}
        </UserAccountTypography>
      </Stack>
    </Box>
  );
};

const AccountRadioButtonLabel = styled(FormControlLabel)(({ theme }) => ({
  padding: theme.spacing(1, 2),
  margin: 0,
}));

const ScrollableUserWrapper = styled(Stack)({
  overflowY: "auto",
  maxHeight: `calc(100vh - ${PAGE_HEADER_HEIGHT}px)`,
});

interface UserAccountLabelProps {
  account: any;
  user: User;
}

const UserAccountPopper = styled(PaperPopper)(({ theme }) => ({
  zIndex: theme.zIndex.drawer,
  "& .MuiPaper-root": {
    marginLeft: theme.spacing(1.25),
    width: "330px",
  },
}));

const UserAccountTypography = styled(Typography)({
  wordBreak: "break-word",
});
